import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Continuous audio recording, transcribed after the user stops.
 *
 * This replaces live speech recognition, and the reason is the whole point of
 * the file: the Web Speech API and Android's SpeechRecognizer are streaming
 * recognisers built for short commands, and they end the session at every
 * pause. Every attempt to hold them open was a restart, and every restart was a
 * gap — speech lost, phrases duplicated, takes ending silently after a second
 * of thinking.
 *
 * MediaRecorder has no concept of silence. It records until it is told to stop,
 * whether that is two seconds later or ten minutes. Nothing can end a take
 * except the user, which is the behaviour that was asked for and the behaviour
 * a dictation actually needs. The words come back once, in order, because they
 * are transcribed once from a finished recording rather than assembled live
 * from a dozen restarted sessions.
 *
 * It also collapses two code paths into one: the packaged app runs this same
 * code in its WebView, so the app and a browser now behave identically instead
 * of driving two different engines through a bridge.
 */

/** Containers worth trying, best first. Chrome and Android give us Opus. */
const PREFERRED_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

/**
 * Safari (and so every iPhone, browser or app) records only MP4/AAC. The
 * readings transcriber cannot decode it; the notes transcriber (ElevenLabs)
 * can, so notes opt in.
 */
const MP4_TYPES = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/aac'];

function pickMimeType(allowMp4 = false): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const type of allowMp4 ? [...PREFERRED_TYPES, ...MP4_TYPES] : PREFERRED_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return null;
}

export function isRecordingSupported(allowMp4 = false): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined' &&
    pickMimeType(allowMp4) !== null
  );
}

export interface RecorderOptions {
  /** Accept Safari's MP4/AAC. Only for consumers whose transcriber reads it. */
  allowMp4?: boolean;
  /** Target bitrate. Speech is clear at 32 kbps and keeps a 2-minute note under 1 MB. */
  bitsPerSecond?: number;
}

export interface Recording {
  blob: Blob;
  mimeType: string;
  seconds: number;
}

interface RecorderState {
  recording: boolean;
  /** Seconds elapsed in the current take, so the UI can prove it is alive. */
  seconds: number;
  /** 0–1, driven off the live signal, for a level meter. */
  level: number;
  error: string | null;
}

export function useRecorder(options: RecorderOptions = {}) {
  const { allowMp4 = false, bitsPerSecond } = options;
  const [state, setState] = useState<RecorderState>({
    recording: false,
    seconds: 0,
    level: 0,
    error: null,
  });

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const tickRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const startedAtRef = useRef(0);
  /** Resolved by stop(), so callers can simply await the finished recording. */
  const resolveRef = useRef<((r: Recording | null) => void) | null>(null);

  const teardown = useCallback(() => {
    if (tickRef.current !== null) window.clearInterval(tickRef.current);
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    tickRef.current = null;
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
  }, []);

  const start = useCallback(async (): Promise<boolean> => {
    if (recorderRef.current) return true;

    const mimeType = pickMimeType(allowMp4);
    if (!mimeType) {
      setState((s) => ({
        ...s,
        error: 'This browser cannot record audio. Type the values instead.',
      }));
      return false;
    }

    let stream: MediaStream;
    const denied = (name: string) => name === 'NotAllowedError' || name === 'SecurityError';
    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            // A lab is noisy and the microphone is usually a phone held at arm's
            // length, so let the platform clean the signal up.
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
      } catch (first) {
        // Some phones and app WebViews refuse the processing constraints while
        // allowing the microphone itself, so try once more with none.
        if (denied((first as { name?: string })?.name ?? '')) throw first;
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
    } catch (e) {
      const name = (e as { name?: string })?.name ?? '';
      console.error('microphone failed', e);
      setState((s) => ({
        ...s,
        recording: false,
        error: denied(name)
          ? 'Microphone permission denied. Allow it in your phone’s settings for BlisxBench, or type instead.'
          : name === 'NotFoundError'
            ? 'No microphone found.'
            : // The name is what tells one failure from another in a screenshot.
              `Could not open the microphone${name ? ` (${name})` : ''}. Close any other app using it and try again.`,
      }));
      return false;
    }

    streamRef.current = stream;
    chunksRef.current = [];

    const recorder = new MediaRecorder(stream, {
      mimeType,
      ...(bitsPerSecond ? { audioBitsPerSecond: bitsPerSecond } : {}),
    });
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      const seconds = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
      const blob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];
      recorderRef.current = null;
      teardown();
      setState({ recording: false, seconds, level: 0, error: null });
      resolveRef.current?.(blob.size > 0 ? { blob, mimeType, seconds } : null);
      resolveRef.current = null;
    };

    recorder.onerror = () => {
      setState((s) => ({ ...s, recording: false, error: 'Recording stopped unexpectedly.' }));
    };

    startedAtRef.current = Date.now();
    /**
     * A timeslice keeps data flowing into chunks rather than being held in one
     * buffer until the end. It does not segment the recording — the chunks are
     * reassembled into a single continuous file — but it means a long take does
     * not sit entirely in memory, and a crash loses a second rather than all of
     * it.
     */
    recorder.start(1000);

    tickRef.current = window.setInterval(() => {
      setState((s) => ({
        ...s,
        seconds: Math.round((Date.now() - startedAtRef.current) / 1000),
      }));
    }, 1000);

    // A level meter, so a silent microphone is visible before the user has
    // talked for two minutes into nothing.
    try {
      const ctx = new AudioContext();
      audioCtxRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      ctx.createMediaStreamSource(stream).connect(analyser);
      const data = new Uint8Array(analyser.frequencyBinCount);
      const sample = () => {
        analyser.getByteTimeDomainData(data);
        let peak = 0;
        for (let i = 0; i < data.length; i++) {
          peak = Math.max(peak, Math.abs(data[i] - 128) / 128);
        }
        setState((s) => (s.recording ? { ...s, level: peak } : s));
        rafRef.current = requestAnimationFrame(sample);
      };
      rafRef.current = requestAnimationFrame(sample);
    } catch {
      // A missing AudioContext costs the meter, never the recording.
    }

    setState({ recording: true, seconds: 0, level: 0, error: null });
    return true;
  }, [teardown, allowMp4, bitsPerSecond]);

  /** Resolves with the finished recording. The only thing that ends a take. */
  const stop = useCallback((): Promise<Recording | null> => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'inactive') {
      teardown();
      setState((s) => ({ ...s, recording: false, level: 0 }));
      return Promise.resolve(null);
    }
    return new Promise<Recording | null>((resolve) => {
      resolveRef.current = resolve;
      try {
        recorder.stop();
      } catch {
        // onstop will not fire; settle here so the caller is never left hanging.
        teardown();
        setState((s) => ({ ...s, recording: false, level: 0 }));
        resolveRef.current = null;
        resolve(null);
      }
    });
  }, [teardown]);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    resolveRef.current = null;
    chunksRef.current = [];
    try {
      if (recorder && recorder.state !== 'inactive') recorder.stop();
    } catch {
      // Already stopped.
    }
    teardown();
    setState({ recording: false, seconds: 0, level: 0, error: null });
  }, [teardown]);

  useEffect(() => () => cancel(), [cancel]);

  return { ...state, start, stop, cancel, supported: isRecordingSupported(allowMp4) };
}
